import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'

export interface BackgroundJobState {
  status: 'pending' | 'processing' | 'completed' | 'failed' | null
  processedCount: number
  totalCount: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  result: any | null
  error: string | null
}

// The background_jobs table is created by migration but may not be in
// generated types yet. Use generic query with type assertion.
interface BackgroundJobRow {
  status: string
  processed_count: number
  total_count: number
  result: unknown
  error: string | null
}

/**
 * Subscribe to Realtime updates on a background_jobs row.
 * Returns live progress so the UI can show a progress bar.
 *
 * Usage:
 *   const { status, processedCount, totalCount, result, error } = useBackgroundJob(jobId)
 */
export function useBackgroundJob(jobId: string | null): BackgroundJobState {
  const [state, setState] = useState<BackgroundJobState>({
    status: jobId ? 'pending' : null,
    processedCount: 0,
    totalCount: 0,
    result: null,
    error: null,
  })

  const fetchInitialState = useCallback(async (id: string) => {
    // Use type assertion since background_jobs may not be in generated types yet
    const { data, error } = await (supabase as any)
      .from('background_jobs')
      .select('status, processed_count, total_count, result, error')
      .eq('id', id)
      .single()

    if (!error && data) {
      const row = data as BackgroundJobRow
      setState({
        status: row.status as BackgroundJobState['status'],
        processedCount: row.processed_count ?? 0,
        totalCount: row.total_count ?? 0,
        result: row.result,
        error: row.error,
      })
    }
  }, [])

  useEffect(() => {
    if (!jobId) {
      setState({ status: null, processedCount: 0, totalCount: 0, result: null, error: null })
      return
    }

    // Fetch current state immediately
    fetchInitialState(jobId)

    // Subscribe to realtime updates
    const channel = supabase
      .channel(`background_job:${jobId}`)
      .on(
        'postgres_changes' as any,
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'background_jobs',
          filter: `id=eq.${jobId}`,
        },
        (payload: any) => {
          const next = payload.new
          if (!next) return

          setState({
            status: next.status as BackgroundJobState['status'],
            processedCount: next.processed_count ?? 0,
            totalCount: next.total_count ?? 0,
            result: next.result,
            error: next.error,
          })
        }
      )
      .subscribe()

    return () => {
      try {
        channel.unsubscribe()
      } catch {
        // noop
      }
    }
  }, [jobId, fetchInitialState])

  return state
}
